import { Link, useLocation, Navigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { SUBURB_LANDINGS, CORE_SERVICES } from "@/lib/suburbLandings";
import { ArrowRight, CheckCircle2, Trees, HardHat, Ruler, Truck, MapPin, Clock, ShieldCheck } from "lucide-react";

const ICONS = { Trees, HardHat, Ruler, Truck };

function LocalBusinessSchema({ suburb }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Site Cleanup ${suburb.name}`,
    "url": `https://sitecleanup.com.au/site-cleanup-${suburb.slug}`,
    "email": "sitecleanup@imperiumtech.com.au",
    "description": suburb.metaDesc,
    "areaServed": {
      "@type": "Place",
      "name": `${suburb.name}, VIC, Australia`,
    },
    "serviceType": [
      "Vacant Lot Cleanup",
      "Builder Waste Removal",
      "Pre-Build Site Preparation",
      "Machinery Coordination",
    ],
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": suburb.name,
      "addressRegion": "VIC",
      "addressCountry": "AU",
    },
  };
  const safe = JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safe }} />;
}

function FAQSchema({ faq }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  const safe = JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safe }} />;
}

export default function SuburbLanding() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/site-cleanup-/, "").replace(/\/$/, "");
  const suburb = slug && SUBURB_LANDINGS[slug];
  if (!suburb) return <Navigate to="/service-areas" replace />;

  const path = `/site-cleanup-${suburb.slug}`;

  return (
    <PublicLayout>
      <Seo title={suburb.metaTitle} description={suburb.metaDesc} path={path} />
      <LocalBusinessSchema suburb={suburb} />
      <FAQSchema faq={suburb.faq} />

      {/* HERO */}
      <section className="bg-asphalt text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 rs-diag opacity-25" />
        <div className="rs-container relative">
          <div className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-widest" data-testid={`landing-breadcrumb-${suburb.slug}`}>
            <Link to="/" className="hover:text-safety">Home</Link>
            <span>/</span>
            <span className="text-white/85">{suburb.name}</span>
          </div>
          <span className="rs-eyebrow text-safety mt-6 block">Site Cleanup · VIC</span>
          <h1 className="mt-3 text-5xl md:text-7xl uppercase font-display leading-[0.95] max-w-4xl" data-testid={`landing-h1-${suburb.slug}`}>
            {suburb.h1}
          </h1>
          <p className="mt-6 max-w-3xl text-white/80 text-base md:text-lg leading-relaxed">
            {suburb.intro[0]}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-quote" className="rs-btn-primary" data-testid={`landing-cta-hero-${suburb.slug}`}>
              Get a free {suburb.name} quote <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/services" className="rs-btn-outline-light">Browse all services</Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-white/70 text-sm">
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-safety" /> Free & no obligation</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-safety" /> Reply within 1 business day</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-safety" /> One matched local contractor</span>
          </div>
        </div>
      </section>

      {/* INTRO CONTINUED */}
      <section className="py-20 bg-white">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <div>
            <span className="rs-eyebrow">About the area</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">Why we service {suburb.name}</h2>
          </div>
          <div className="md:col-span-2 space-y-4 text-asphalt text-base leading-relaxed">
            {suburb.intro.slice(1).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 bg-concrete border-y border-border">
        <div className="rs-container">
          <div className="max-w-2xl">
            <span className="rs-eyebrow">Services in {suburb.name}</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">The four jobs we quote on most</h2>
            <p className="mt-4 text-muted-foreground">Every {suburb.name} quote is scoped from your photos and address. These are the core service lines our matched contractors handle.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CORE_SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || CheckCircle2;
              return (
                <div key={s.title} className="rs-card h-full" data-testid={`landing-service-${suburb.slug}-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="w-11 h-11 grid place-items-center bg-safety/10 text-safety">
                    <Icon size={20} />
                  </span>
                  <p className="mt-4 font-display uppercase text-lg leading-tight">{s.title}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <div>
            <span className="rs-eyebrow">FAQ · {suburb.name}</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">Common questions from {suburb.name} enquirers</h2>
            <p className="mt-4 text-muted-foreground">Something else on your mind? Add it to the "message" field in the quote form and we'll answer with your quote.</p>
          </div>
          <div className="md:col-span-2 space-y-4">
            {suburb.faq.map((f, i) => (
              <div key={i} className="border-l-2 border-safety pl-5 py-2" data-testid={`landing-faq-${suburb.slug}-${i}`}>
                <p className="font-display uppercase text-base tracking-wide">{f.q}</p>
                <p className="mt-2 text-sm text-asphalt leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-safety text-white">
        <div className="rs-container grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="rs-eyebrow text-white/80">Ready when you are</span>
            <h2 className="mt-3 text-3xl md:text-5xl uppercase font-display leading-tight">Free {suburb.name} site cleanup quote.</h2>
            <p className="mt-3 text-white/90 max-w-lg">Send a few photos and your street address. We'll come back within one business day with a matched local contractor and a written quote.</p>
            <div className="mt-6 flex items-center gap-2 text-white/80 text-sm">
              <MapPin size={14} /> Covering {suburb.name} & surrounding suburbs
            </div>
          </div>
          <div className="md:justify-self-end">
            <Link
              to="/request-quote"
              className="inline-flex items-center gap-2 bg-asphalt text-white px-8 py-4 font-semibold uppercase tracking-widest border-2 border-asphalt hover:bg-white hover:text-asphalt transition-colors"
              data-testid={`landing-cta-final-${suburb.slug}`}
            >
              Get my {suburb.name} quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
